// public/js/features/display/template-editor.js
// The editor behind every configurable display surface
// (docs/asset-display-templates.md). One modal, three body layouts:
//
//   listColumns / assetDetail → a reorderable list with visibility checkboxes
//   mapCard                   → block editor + variable palette + live preview
//   pinLabel                  → one-line template + palette + live preview + cap
//
// WHY blocks and not free drag-and-drop layout: a block list stays renderable on
// a phone, survives a schema change, and still lets a tenant put its own fields
// on the card. WHY both drag AND up/down buttons: drag-and-drop is unusable with
// a keyboard and unreliable on touch, so the buttons are the real control and
// the drag is the shortcut.
//
// The editor never renders a surface itself — it produces a template value and
// hands it to template-store.js. The live preview uses the SAME renderTemplate()
// the real surfaces use, so what you see is what the map will draw.

import { escapeHtml } from '../../utils/escape.js';
import {
  SURFACES, SURFACE_META, PIN_LABEL_HARD_MAX,
  loadTemplates, getTemplate, hasUserOverride,
  saveTenantTemplate, saveUserTemplate, resetUserTemplate,
  availableVariables, renderTemplate, SAMPLE_ASSET,
  listColumnCandidates, assetDetailCandidates,
  getRegistryDefs, getLabelTenant,
} from './template-store.js';
import { injectDisplayStyles } from './display.css.js';

const MODAL_ID = 'display-template-editor';

// The row-actions column must stay reachable, so its visibility is fixed.
const ALWAYS_VISIBLE = new Set(['actions']);

const BLOCK_META = {
  title:   { label: 'Title',   icon: 'fa-heading',       hint: 'The card header. Empty keeps the asset reference, with the name on the line below it; or build your own line with the fields below.' },
  photo:   { label: 'Photo',   icon: 'fa-image',         hint: 'The asset photo (shown once photos are available).' },
  text:    { label: 'Text',    icon: 'fa-align-left',    hint: 'Free lines of text and fields — one line per row.' },
  sensors: { label: 'Sensors', icon: 'fa-temperature-half', hint: 'The live sensor readings of the paired devices.' },
  buttons: { label: 'Buttons', icon: 'fa-square-arrow-up-right', hint: 'Open asset page / load history / add photo. The Google Maps link sits next to the coordinates and is always there.' },
};

const cssEsc = (s) => (window.CSS && CSS.escape ? CSS.escape(String(s)) : String(s).replace(/["\\]/g, '\\$&'));

// ── draft state ─────────────────────────────────────────────────────────────
// The modal edits a plain draft object and only serialises it on save, so
// Cancel is always a true no-op.
function orderedRows(candidates, template, listKey) {
  const cfg = new Map((template?.[listKey] || []).map(c => [c.key, c]));
  return candidates
    .map((c, i) => {
      const hit = cfg.get(c.key);
      return {
        key: c.key,
        label: c.label,
        kind: c.kind,
        visible: hit ? hit.visible !== false : true,
        order: hit && Number.isFinite(hit.order) ? hit.order : (c.defaultOrder ?? (i + 1) * 10),
      };
    })
    .sort((a, b) => a.order - b.order);
}

let _uid = 0;
function makeDraft(surface, template, regDefs, tenant) {
  if (surface === SURFACES.listColumns) {
    return { kind: 'ordered', listKey: 'columns', rows: orderedRows(listColumnCandidates(regDefs, tenant), template, 'columns') };
  }
  if (surface === SURFACES.assetDetail) {
    return { kind: 'ordered', listKey: 'fields', rows: orderedRows(assetDetailCandidates(regDefs, tenant), template, 'fields') };
  }
  if (surface === SURFACES.mapCard) {
    return {
      kind: 'blocks',
      blocks: (template.blocks || []).map(b => ({
        uid: `b${++_uid}`,
        type: b.type,
        visible: b.visible !== false,
        lines: b.type === 'text' ? (b.lines || []).slice() : undefined,
        template: b.type === 'title' ? (b.template || '') : undefined,
      })),
    };
  }
  return { kind: 'pin', template: template.template || '', maxLength: template.maxLength || 16 };
}

function serialise(surface, draft) {
  if (draft.kind === 'ordered') {
    return { [draft.listKey]: draft.rows.map((r, i) => ({ key: r.key, visible: !!r.visible, order: (i + 1) * 10 })) };
  }
  if (draft.kind === 'blocks') {
    return {
      blocks: draft.blocks.map(b => {
        if (b.type === 'text') return { type: 'text', visible: !!b.visible, lines: (b.lines || []).map(l => l.trim()).filter(Boolean) };
        // The title may carry its own one-line template (empty = the asset label).
        if (b.type === 'title') return { type: 'title', visible: !!b.visible, template: (b.template || '').trim() };
        return { type: b.type, visible: !!b.visible };
      }),
    };
  }
  return { template: draft.template.trim(), maxLength: draft.maxLength };
}

// ── markup helpers ──────────────────────────────────────────────────────────
function moveButtonsHtml(i, total) {
  return `
    <div class="dt-row-actions btn-group btn-group-sm" role="group">
      <button type="button" class="btn btn-outline-secondary dt-up" aria-label="Move up" title="Move up" ${i === 0 ? 'disabled' : ''}>
        <i class="fa-solid fa-chevron-up"></i>
      </button>
      <button type="button" class="btn btn-outline-secondary dt-down" aria-label="Move down" title="Move down" ${i === total - 1 ? 'disabled' : ''}>
        <i class="fa-solid fa-chevron-down"></i>
      </button>
    </div>`;
}

function rowHtml(row, i, total) {
  const locked = ALWAYS_VISIBLE.has(row.key);
  const id = `dt-vis-${row.key}`;
  return `
    <li class="dt-row ${row.visible ? '' : 'dt-hidden-row'}" draggable="true" data-key="${escapeHtml(row.key)}">
      <span class="dt-grip" aria-hidden="true"><i class="fa-solid fa-grip-vertical"></i></span>
      <div class="dt-row-main">
        <div class="form-check mb-0">
          <input class="form-check-input dt-vis" type="checkbox" id="${escapeHtml(id)}"
                 ${row.visible ? 'checked' : ''} ${locked ? 'disabled' : ''}>
          <label class="form-check-label dt-row-label" for="${escapeHtml(id)}">${escapeHtml(row.label)}</label>
        </div>
        <span class="dt-row-key">${escapeHtml(row.key)}</span>
        ${locked ? '<span class="badge badge-soft-gray">always shown</span>' : ''}
      </div>
      ${moveButtonsHtml(i, total)}
    </li>`;
}

function blockHtml(block, i, total) {
  const meta = BLOCK_META[block.type] || { label: block.type, icon: 'fa-cube', hint: '' };
  const id = `dt-block-vis-${block.uid}`;
  return `
    <li class="dt-row dt-block ${block.visible ? '' : 'dt-hidden-row'}" draggable="true" data-key="${escapeHtml(block.uid)}">
      <div class="dt-block-head">
        <span class="dt-grip" aria-hidden="true"><i class="fa-solid fa-grip-vertical"></i></span>
        <span class="dt-block-title"><i class="fa-solid ${escapeHtml(meta.icon)} me-2 text-secondary"></i>${escapeHtml(meta.label)}</span>
        <div class="form-check form-switch mb-0">
          <input class="form-check-input dt-vis" type="checkbox" role="switch" id="${escapeHtml(id)}" ${block.visible ? 'checked' : ''}>
          <label class="form-check-label small text-muted" for="${escapeHtml(id)}">Show</label>
        </div>
        ${moveButtonsHtml(i, total)}
        ${block.type === 'text'
          ? `<button type="button" class="btn btn-sm btn-outline-danger dt-remove" aria-label="Remove text block" title="Remove this text block"><i class="fa-solid fa-trash"></i></button>`
          : ''}
      </div>
      ${block.type === 'title' ? `
      <div class="dt-block-body">
        <input type="text" class="form-control form-control-sm dt-title" data-tpl-input
               placeholder="Leave empty for the asset name, e.g. \${reference} - \${engine_serialNo}"
               aria-label="Card title template" value="${escapeHtml(block.template || '')}">
        <div class="dt-hint mt-1">${escapeHtml(meta.hint)}</div>
      </div>` : ''}
      ${block.type === 'text' ? `
      <div class="dt-block-body">
        <textarea class="form-control form-control-sm dt-lines" data-tpl-input rows="${Math.max(2, (block.lines || []).length)}"
                  placeholder="One line per row, e.g. Ref: \${reference}"
                  aria-label="Text block lines">${escapeHtml((block.lines || []).join('\n'))}</textarea>
      </div>` : (block.type === 'title' ? '' : `<div class="dt-block-body dt-hint">${escapeHtml(meta.hint)}</div>`)}
    </li>`;
}

function paletteHtml(vars) {
  const groups = [];
  for (const v of vars) if (!groups.includes(v.group)) groups.push(v.group);
  return `
    <div class="dt-palette">
      <div class="d-flex align-items-center gap-2 mb-1 flex-wrap">
        <span class="fw-semibold small">Insert a field</span>
        <span class="text-muted" style="font-size:.72rem;">click to insert at the cursor</span>
        <input type="search" class="form-control form-control-sm ms-auto dt-var-filter" placeholder="Filter…"
               style="max-width:160px;" aria-label="Filter fields">
      </div>
      <div class="dt-palette-groups">
        ${groups.map(g => `
          <div class="dt-palette-group">
            <div class="dt-palette-group-title">${escapeHtml(g)}</div>
            ${vars.filter(v => v.group === g).map(v => `
              <button type="button" class="dt-var" data-var="${escapeHtml(v.id)}"
                      data-search="${escapeHtml(`${v.label} ${v.id}`.toLowerCase())}"
                      title="\${${escapeHtml(v.id)}} — for example: ${escapeHtml(v.sample)}">${escapeHtml(v.label)}</button>`).join('')}
          </div>`).join('')}
      </div>
      <div class="dt-hint mt-2">
        An empty field prints nothing, and a line whose fields are all empty disappears — so a card never shows a stray separator.
      </div>
    </div>`;
}

// ── live previews ───────────────────────────────────────────────────────────
const SAMPLE_SENSOR_BADGES = `
  <span class="badge bg-light text-dark border me-1 mb-1">Temp: 21.4 °C</span>
  <span class="badge bg-light text-dark border me-1 mb-1">Battery: 87 %</span>`;

// The card head in the preview: the configured title template, else the same
// fallback the real card uses (modules/map/info-panel.js → cardHeading).
function titleFallback(asset) {
  return asset.reference || asset.serial || asset.label || asset.name || 'Unnamed Asset';
}

function titlePreview(block, asset) {
  const tpl = (block.template || '').trim();
  if (!tpl) return titleFallback(asset);
  try { return renderTemplate(tpl, asset, { plain: true, maxLength: 120 }) || titleFallback(asset); }
  catch { return titleFallback(asset); }
}

// The name under the header, dropped when the header already says it.
function subtitlePreview(block, asset) {
  const name = asset.label || asset.name || '';
  return name && name !== titlePreview(block, asset) ? name : '';
}

function cardPreviewHtml(draft, asset) {
  const blocks = draft.blocks.filter(b => b.visible);
  const titleBlock = blocks.find(b => b.type === 'title');
  const body = blocks.filter(b => b.type !== 'title').map(b => {
    if (b.type === 'photo') return `<div class="dt-preview-photo"><i class="fa-regular fa-image fa-lg"></i></div>`;
    if (b.type === 'text') {
      const html = renderTemplate((b.lines || []).join('\n'), asset);
      return `<div class="dt-preview-text mb-2">${html || '<span class="dt-preview-empty">(nothing to show for this asset)</span>'}</div>`;
    }
    if (b.type === 'sensors') return `<hr class="my-2"><div class="small"><strong>Environment &amp; sensors</strong><div class="mt-1">${SAMPLE_SENSOR_BADGES}</div></div>`;
    if (b.type === 'buttons') {
      return `<div class="mt-3 d-flex flex-column gap-2">
        <span class="btn btn-sm btn-primary w-100 disabled"><i class="fa-solid fa-up-right-from-square me-1"></i>Open asset page</span>
        <span class="d-flex gap-2">
          <span class="btn btn-sm btn-outline-secondary flex-grow-1 disabled"><i class="fa-regular fa-calendar-days me-1"></i>History</span>
          <span class="btn btn-sm btn-outline-secondary flex-grow-1 disabled"><i class="fa-solid fa-camera me-1"></i>Photo</span>
        </span>
      </div>`;
    }
    return '';
  }).join('');

  return `
    <div class="dt-preview-card">
      ${titleBlock ? `<div class="dt-preview-card-head">${escapeHtml(titlePreview(titleBlock, asset))}</div>
        ${subtitlePreview(titleBlock, asset) ? `<div class="dt-preview-card-sub">${escapeHtml(subtitlePreview(titleBlock, asset))}</div>` : ''}` : ''}
      <div class="dt-preview-card-body">${body || '<span class="dt-preview-empty">All blocks are hidden.</span>'}</div>
    </div>`;
}

function pinPreviewHtml(draft, asset) {
  const text = renderTemplate(draft.template, asset, { plain: true, maxLength: draft.maxLength });
  return `
    <div class="dt-pin">
      <span class="dt-pin-dot"></span>
      ${text ? `<span class="dt-pin-label">${escapeHtml(text)}</span>` : '<span class="dt-preview-empty small">(no label for this asset)</span>'}
    </div>
    <div class="text-center dt-hint mt-2">${text.length} / ${draft.maxLength} characters</div>`;
}

// ── the modal ───────────────────────────────────────────────────────────────
/**
 * Open the editor for one display surface.
 * @param {object} opts
 * @param {string} opts.surface  one of SURFACES
 * @param {string|null} opts.tenantId  the tenant whose default may be edited
 * @param {boolean} opts.canEditTenantDefault  show "Save for everyone"
 * @param {(info:{surface:string,scope:'tenant'|'user'|'reset',value:object})=>void} [opts.onSaved]
 * @param {object} [opts.sampleAsset]  a real asset for the live preview
 */
export async function openTemplateEditor({ surface, tenantId = null, canEditTenantDefault = false, onSaved, sampleAsset = null } = {}) {
  const meta = SURFACE_META[surface];
  if (!meta) { window.showToast?.('Unknown display surface', 'warning'); return; }

  injectDisplayStyles();
  try { await loadTemplates(tenantId); } catch { /* defaults are fine */ }

  const regDefs = getRegistryDefs();
  const tenant  = getLabelTenant();
  const asset   = sampleAsset || SAMPLE_ASSET;
  const vars    = availableVariables(regDefs, tenant);
  const draft   = makeDraft(surface, getTemplate(surface), regDefs, tenant);
  const overridden = hasUserOverride(surface);
  const wide = surface === SURFACES.mapCard;

  document.getElementById(MODAL_ID)?.remove();
  document.body.insertAdjacentHTML('beforeend', `
    <div class="modal fade" id="${MODAL_ID}" tabindex="-1" aria-hidden="true" aria-labelledby="${MODAL_ID}-title">
      <div class="modal-dialog ${wide ? 'modal-xl' : 'modal-lg'} modal-dialog-centered modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <div>
              <h5 class="modal-title" id="${MODAL_ID}-title"><i class="fa-solid ${escapeHtml(meta.icon)} me-2 text-secondary"></i>${escapeHtml(meta.title)}</h5>
              <div class="text-muted small">${escapeHtml(meta.description)}</div>
            </div>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body" id="dt-editor-body"></div>
          <div class="modal-footer">
            <div class="dt-footer">
              <button type="button" class="btn btn-outline-secondary btn-sm dt-reset" ${overridden ? '' : 'hidden'}>
                <i class="fa-solid fa-rotate-left me-1"></i>Reset to tenant default
              </button>
              <span class="dt-footer-spacer"></span>
              <button type="button" class="btn btn-light" data-bs-dismiss="modal">Cancel</button>
              ${canEditTenantDefault && tenantId
                ? `<button type="button" class="btn btn-outline-primary dt-save-tenant"><i class="fa-solid fa-building me-1"></i>Save for everyone</button>` : ''}
              <button type="button" class="btn btn-primary dt-save-user"><i class="fa-solid fa-user me-1"></i>Save for me</button>
            </div>
          </div>
        </div>
      </div>
    </div>`);

  const el = document.getElementById(MODAL_ID);
  const body = el.querySelector('#dt-editor-body');
  body.innerHTML = bodyHtml(surface, draft, vars, overridden);

  const preview = body.querySelector('#dt-preview');
  const refreshPreview = () => {
    if (!preview) return;
    preview.innerHTML = draft.kind === 'blocks' ? cardPreviewHtml(draft, asset) : pinPreviewHtml(draft, asset);
  };
  refreshPreview();

  if (draft.kind === 'ordered' || draft.kind === 'blocks') wireList(body, draft, refreshPreview);
  if (draft.kind === 'blocks') wireBlockExtras(body, draft, refreshPreview);
  if (draft.kind === 'pin') wirePin(body, draft, refreshPreview);
  if (draft.kind !== 'ordered') wirePalette(body);

  // ── save / reset ──────────────────────────────────────────────────────────
  const footerBtns = () => Array.from(el.querySelectorAll('.modal-footer button'));
  const busy = (on) => footerBtns().forEach(b => { b.disabled = on; });

  const finish = (scope, value) => {
    modal.hide();
    onSaved?.({ surface, scope, value });
  };

  el.querySelector('.dt-save-user')?.addEventListener('click', async () => {
    busy(true);
    try {
      const value = await saveUserTemplate(surface, serialise(surface, draft));
      window.showToast?.('Saved for you', 'success');
      finish('user', value);
    } catch (e) {
      window.showToast?.(e?.message || 'Save failed — please try again', 'danger');
      busy(false);
    }
  });

  el.querySelector('.dt-save-tenant')?.addEventListener('click', async () => {
    busy(true);
    try {
      const value = await saveTenantTemplate(tenantId, surface, serialise(surface, draft));
      window.showToast?.('Saved as the tenant default', 'success');
      finish('tenant', value);
    } catch (e) {
      window.showToast?.(e?.message || 'Save failed — please try again', 'danger');
      busy(false);
    }
  });

  el.querySelector('.dt-reset')?.addEventListener('click', async () => {
    busy(true);
    try {
      const value = await resetUserTemplate(surface);
      window.showToast?.('Your personal version was removed — the tenant default applies again', 'success');
      finish('reset', value);
    } catch (e) {
      window.showToast?.(e?.message || 'Reset failed — please try again', 'danger');
      busy(false);
    }
  });

  el.addEventListener('hidden.bs.modal', () => el.remove(), { once: true });
  const modal = new bootstrap.Modal(el);
  modal.show();
}

// ── body layouts ────────────────────────────────────────────────────────────
function bodyHtml(surface, draft, vars, overridden) {
  const overrideNote = overridden
    ? `<div class="alert alert-info py-2 px-3 small d-flex align-items-center gap-2 mb-3">
         <i class="fa-solid fa-user"></i>
         <span>You have a personal version of this view. It hides any change an administrator makes until you reset it.</span>
       </div>`
    : '';

  if (draft.kind === 'ordered') {
    return `
      ${overrideNote}
      <p class="dt-hint mb-3">
        Tick what you want to see and drag the handle — or use the arrow buttons — to change the order.
      </p>
      <ul class="dt-list" id="dt-rows">${draft.rows.map((r, i) => rowHtml(r, i, draft.rows.length)).join('')}</ul>`;
  }

  if (draft.kind === 'blocks') {
    return `
      ${overrideNote}
      <div class="row g-4">
        <div class="col-lg-7">
          <p class="dt-hint mb-3">Order the blocks of the card, switch off what you do not need, and write your own text lines.</p>
          <ul class="dt-list" id="dt-rows">${draft.blocks.map((b, i) => blockHtml(b, i, draft.blocks.length)).join('')}</ul>
          <button type="button" class="btn btn-sm btn-outline-secondary dt-add-text mb-3">
            <i class="fa-solid fa-plus me-1"></i>Add a text block
          </button>
          ${paletteHtml(vars)}
        </div>
        <div class="col-lg-5">
          <div class="dt-preview-pane">
            <div class="dt-preview-label">Live preview</div>
            <div class="dt-preview-stage"><div id="dt-preview" style="width:100%;"></div></div>
            <div class="dt-hint mt-2">Rendered with a sample asset — real cards use the asset you clicked.</div>
          </div>
        </div>
      </div>`;
  }

  return `
    ${overrideNote}
    <div class="row g-4">
      <div class="col-lg-7">
        <label class="form-label small fw-semibold" for="dt-pin-input">Label template</label>
        <input type="text" class="form-control dt-pin-input" id="dt-pin-input" data-tpl-input
               value="${escapeHtml(draft.template)}" placeholder="\${label}">
        <div class="dt-hint mt-1">One line only. Example: <code>\${reference} · \${engine_serialNo}</code></div>

        <label class="form-label small fw-semibold mt-3" for="dt-pin-max">
          Maximum length: <span class="dt-pin-max-out">${draft.maxLength}</span> characters
        </label>
        <input type="range" class="form-range dt-pin-max" id="dt-pin-max" min="6" max="${PIN_LABEL_HARD_MAX}" value="${draft.maxLength}">
        <div class="dt-hint">Longer labels are cut with an ellipsis so pins stay readable when they sit close together.</div>

        <div class="mt-3">${paletteHtml(vars)}</div>
      </div>
      <div class="col-lg-5">
        <div class="dt-preview-pane">
          <div class="dt-preview-label">Live preview</div>
          <div class="dt-preview-stage"><div id="dt-preview" style="width:100%;"></div></div>
        </div>
      </div>
    </div>`;
}

// ── wiring ──────────────────────────────────────────────────────────────────
// Reorder + visibility for both list flavours. Up/down repaint the list (and
// restore focus on the button that was pressed, so a keyboard user can keep
// pressing it); the checkbox only toggles a class, so nothing steals focus.
function wireList(root, draft, refreshPreview) {
  const host = root.querySelector('#dt-rows');
  if (!host) return;
  const items = () => (draft.kind === 'ordered' ? draft.rows : draft.blocks);
  const keyOf = (it) => (draft.kind === 'ordered' ? it.key : it.uid);
  const render = (it, i, total) => (draft.kind === 'ordered' ? rowHtml(it, i, total) : blockHtml(it, i, total));

  const paint = (focusKey, focusSel) => {
    const list = items();
    host.innerHTML = list.map((it, i) => render(it, i, list.length)).join('');
    if (focusKey) {
      const btn = host.querySelector(`[data-key="${cssEsc(focusKey)}"] ${focusSel}`);
      if (btn && !btn.disabled) btn.focus();
      else host.querySelector(`[data-key="${cssEsc(focusKey)}"] .dt-grip`)?.focus?.();
    }
    refreshPreview();
  };
  host.__paint = paint;

  const indexOfKey = (key) => items().findIndex(it => keyOf(it) === key);
  const move = (from, to) => {
    const list = items();
    if (from < 0 || to < 0 || to >= list.length || from === to) return false;
    const [it] = list.splice(from, 1);
    list.splice(to, 0, it);
    return true;
  };

  host.addEventListener('click', (e) => {
    const btn = e.target.closest('.dt-up, .dt-down');
    if (!btn) return;
    const row = btn.closest('[data-key]');
    if (!row) return;
    const up = btn.classList.contains('dt-up');
    const i = indexOfKey(row.dataset.key);
    if (move(i, i + (up ? -1 : 1))) paint(row.dataset.key, up ? '.dt-up' : '.dt-down');
  });

  host.addEventListener('change', (e) => {
    const cb = e.target.closest('.dt-vis');
    if (!cb) return;
    const row = cb.closest('[data-key]');
    const it = items().find(x => keyOf(x) === row?.dataset.key);
    if (!it) return;
    it.visible = cb.checked;
    row.classList.toggle('dt-hidden-row', !cb.checked);
    refreshPreview();
  });

  // ── drag and drop (the shortcut; the buttons above are the real control) ──
  let dragKey = null;
  const clearMarks = () => host.querySelectorAll('.dt-drop-before, .dt-drop-after')
    .forEach(n => n.classList.remove('dt-drop-before', 'dt-drop-after'));

  host.addEventListener('dragstart', (e) => {
    const row = e.target.closest('[data-key]');
    if (!row) return;
    dragKey = row.dataset.key;
    row.classList.add('dt-dragging');
    try { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', dragKey); } catch {}
  });

  host.addEventListener('dragover', (e) => {
    if (!dragKey) return;
    e.preventDefault();
    try { e.dataTransfer.dropEffect = 'move'; } catch {}
    const row = e.target.closest('[data-key]');
    clearMarks();
    if (!row || row.dataset.key === dragKey) return;
    const box = row.getBoundingClientRect();
    row.classList.add(e.clientY < box.top + box.height / 2 ? 'dt-drop-before' : 'dt-drop-after');
  });

  host.addEventListener('dragleave', (e) => {
    if (!host.contains(e.relatedTarget)) clearMarks();
  });

  host.addEventListener('drop', (e) => {
    if (!dragKey) return;
    e.preventDefault();
    const row = e.target.closest('[data-key]');
    const key = dragKey;
    dragKey = null;
    if (!row || row.dataset.key === key) { clearMarks(); paint(); return; }
    const box = row.getBoundingClientRect();
    const after = e.clientY >= box.top + box.height / 2;
    const from = indexOfKey(key);
    let to = indexOfKey(row.dataset.key) + (after ? 1 : 0);
    if (from < to) to -= 1;
    move(from, to);
    clearMarks();
    paint(key, '.dt-up');
  });

  host.addEventListener('dragend', () => { dragKey = null; clearMarks(); host.querySelectorAll('.dt-dragging').forEach(n => n.classList.remove('dt-dragging')); });
}

// Text-block editing: typing into a block's textarea, adding and removing blocks.
function wireBlockExtras(root, draft, refreshPreview) {
  const host = root.querySelector('#dt-rows');

  host.addEventListener('input', (e) => {
    const ti = e.target.closest('.dt-title');
    if (ti) {
      const b = draft.blocks.find(x => x.uid === ti.closest('[data-key]')?.dataset.key);
      if (b) { b.template = ti.value; refreshPreview(); }
      return;
    }
    const ta = e.target.closest('.dt-lines');
    if (!ta) return;
    const block = draft.blocks.find(b => b.uid === ta.closest('[data-key]')?.dataset.key);
    if (!block) return;
    block.lines = ta.value.split('\n');
    refreshPreview();
  });

  host.addEventListener('click', (e) => {
    const rm = e.target.closest('.dt-remove');
    if (!rm) return;
    const key = rm.closest('[data-key]')?.dataset.key;
    const i = draft.blocks.findIndex(b => b.uid === key);
    if (i < 0) return;
    draft.blocks.splice(i, 1);
    host.__paint?.();
  });

  root.querySelector('.dt-add-text')?.addEventListener('click', () => {
    draft.blocks.push({ uid: `b${++_uid}`, type: 'text', visible: true, lines: [''] });
    host.__paint?.();
    // Put the cursor straight into the new block — the user clicked "add" to type.
    const areas = host.querySelectorAll('.dt-lines');
    areas[areas.length - 1]?.focus();
  });
}

function wirePin(root, draft, refreshPreview) {
  const input = root.querySelector('.dt-pin-input');
  const range = root.querySelector('.dt-pin-max');
  const out = root.querySelector('.dt-pin-max-out');
  input.addEventListener('input', () => {
    // A pin label is one line by definition — paste of a multi-line value collapses.
    if (input.value.includes('\n')) input.value = input.value.replace(/[\r\n]+/g, ' ');
    draft.template = input.value;
    refreshPreview();
  });
  range.addEventListener('input', () => {
    draft.maxLength = parseInt(range.value, 10) || 16;
    out.textContent = draft.maxLength;
    refreshPreview();
  });
}

// The variable palette inserts `${fieldId}` at the caret of the input the user
// last worked in. mousedown is cancelled on the palette buttons so the click
// never steals focus (and therefore never loses the caret position).
function wirePalette(root) {
  const palette = root.querySelector('.dt-palette');
  if (!palette) return;
  let last = root.querySelector('[data-tpl-input]');

  root.addEventListener('focusin', (e) => {
    const el = e.target.closest('[data-tpl-input]');
    if (el) last = el;
  });

  palette.addEventListener('mousedown', (e) => { if (e.target.closest('.dt-var')) e.preventDefault(); });

  palette.addEventListener('click', (e) => {
    const btn = e.target.closest('.dt-var');
    if (!btn) return;
    const active = document.activeElement;
    const target = (active && active.matches?.('[data-tpl-input]')) ? active : last;
    if (!target) { window.showToast?.('Click into a text field first', 'info'); return; }
    const token = '${' + btn.dataset.var + '}';
    const start = target.selectionStart ?? target.value.length;
    const end = target.selectionEnd ?? start;
    target.value = target.value.slice(0, start) + token + target.value.slice(end);
    const caret = start + token.length;
    target.focus();
    try { target.setSelectionRange(caret, caret); } catch {}
    target.dispatchEvent(new Event('input', { bubbles: true }));
    last = target;
  });

  const filter = palette.querySelector('.dt-var-filter');
  filter?.addEventListener('input', () => {
    const q = filter.value.trim().toLowerCase();
    palette.querySelectorAll('.dt-palette-group').forEach(group => {
      let any = false;
      group.querySelectorAll('.dt-var').forEach(btn => {
        const hit = !q || (btn.dataset.search || '').includes(q);
        btn.hidden = !hit;
        if (hit) any = true;
      });
      group.hidden = !any;
    });
  });
}
